/**
 * Policy Enhanced Component
 * 
 * Unique Properties:
 * - Policy types (Guard, Authorization, BusinessRule, Validation, Compliance)
 * - Policy scope (Global, Tenant, Workflow, Field, Entity)
 * - CEL expressions and business rule conditions
 * - Rule priority and enforcement modes
 * - Version control and approval workflows
 * - Compliance tracking and audit trail
 */
// @ts-nocheck


import { TEST_POLICY, useMutation } from '@ferp/graphql-client';
import {
    Accordion,
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Code,
    Container,
    Divider,
    Grid,
    Group,
    JsonInput,
    List,
    Modal,
    NumberInput,
    Paper,
    Progress,
    RingProgress,
    SegmentedControl,
    Select,
    Stack,
    Table,
    Tabs,
    Text,
    TextInput,
    Textarea,
    ThemeIcon,
    Timeline,
    Title
} from '@mantine/core';
import '@mantine/core/styles.css';
import { notifications } from '@mantine/notifications';
import {
    IconAlertTriangle,
    IconBuilding,
    IconBusinessplan,
    IconCheck,
    IconDatabase,
    IconEdit,
    IconEye,
    IconFileCheck,
    IconGitBranch,
    IconGlobe,
    IconHistory,
    IconInfoCircle,
    IconLock,
    IconPlayerPlay,
    IconPlus,
    IconScale,
    IconShield,
    IconShieldCheck,
    IconTextCaption,
    IconUserCheck
} from '@tabler/icons-react';
import React, { useEffect, useRef, useState } from 'react';
import { TabulatorFull } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_midnight.min.css';
import { usePolicyEvaluations } from '../hooks/useSubscriptions';
import { usePolicies } from '../hooks/useWorkflowState';
import { usePolicyMutation } from '../hooks/useWorkflowMutation';
import WorkflowSearchMenu from './WorkflowSearchMenu';

interface PolicyRule {
    condition: string;
    action: string;
    priority: number;
    description?: string;
}

interface PolicyProps {
    id?: string;
    data?: any;
    mode?: 'view' | 'edit' | 'create';
    onSave?: (data: any) => void;
}

export const PolicyEnhanced: React.FC<PolicyProps> = ({
    id,
    data = {},
    mode = 'edit',
    onSave
}) => {
    const [activeTab, setActiveTab] = useState('rules');
    const [policyType, setPolicyType] = useState(data?.policyType || 'SECURITY');  // Updated from 'GUARD' to 'SECURITY'
    const [policyScope, setPolicyScope] = useState(data.scope || 'GLOBAL');
    const [rules, setRules] = useState<PolicyRule[]>(data.rules || []);
    const [implementation, setImplementation] = useState(data.implementation || '');
    const [modalOpened, setModalOpened] = useState(false);
    const [testModalOpen, setTestModalOpen] = useState(false);
    const [selectedRule, setSelectedRule] = useState<PolicyRule | null>(null);
    const [policyMetadata, setPolicyMetadata] = useState({
        name: data.name || '',
        description: data.description || '',
        priority: data.priority || 100,
        enforcement_mode: data.enforcement_mode || 'STRICT'
    });
    const [testData, setTestData] = useState('{}');
    const [testResult, setTestResult] = useState(null);
    const [evaluationHistory, setEvaluationHistory] = useState<any[]>([]);
    const [complianceData, setComplianceData] = useState<any>({ violations: 0, approvals: 0, totalEvaluations: 0 });

    const rulesTableRef = useRef<HTMLDivElement>(null);
    const rulesTabulator = useRef<InstanceType<typeof TabulatorFull> | null>(null);

    // Real data hooks
    const { items: policies, loading, error, refetch } = usePolicies();
    const { save: createPolicy, loading: creating } = usePolicyMutation();
    const [testPolicy, { loading: testing }] = useMutation<Record<string, any>>(TEST_POLICY);

    // Real-time subscription for policy evaluations
    const policyEvaluations = usePolicyEvaluations(
        { tenant: 'test', policyId: data.id },
        {
            onData: (data) => {
                console.log('Policy evaluation:', data);

                // Update evaluation history
                setEvaluationHistory(prev => [{
                    id: data.evaluationId,
                    timestamp: data.timestamp,
                    result: data.evaluationResult,
                    instanceId: data?.instanceId,
                    executionTime: data.executionTime,
                    error: data.errorDetails
                }, ...prev]);

                // Update compliance tracking
                if (data.evaluationResult === 'DENY') {
                    setComplianceData((prev: any) => ({
                        ...prev,
                        violations: prev.violations + 1,
                        lastViolation: data.timestamp
                    }));
                } else if (data.evaluationResult === 'ALLOW') {
                    setComplianceData((prev: any) => ({
                        ...prev,
                        approvals: prev.approvals + 1,
                        totalEvaluations: prev.totalEvaluations + 1
                    }));
                }
            }
        },
        { enabled: true, showWarnings: true, showErrors: true }
    );

    // Policy type configuration
    // UPDATED: New PolicyType enum values (Phase 1 migration complete)
    const policyTypeConfig = {
        // NEW types (Phase 1 migration)
        APPROVAL: { icon: <IconUserCheck />, color: 'indigo', description: 'Approval routing & dual control (NEW)' },
        SECURITY: { icon: <IconShield />, color: 'red', description: 'Access control & authorization (REPLACES GUARD/AUTHORIZATION)' },
        NOTIFICATION: { icon: <IconAlertTriangle />, color: 'yellow', description: 'Communication routing (NEW)' },

        // Existing types
        VALIDATION: { icon: <IconCheck />, color: 'purple', description: 'Data validation & business rules' },
        COMPLIANCE: { icon: <IconScale />, color: 'grape', description: 'Regulatory compliance verification' },

        // DEPRECATED types (backward compatibility)
        GUARD: { icon: <IconShield />, color: 'blue', description: 'Workflow transition guards (DEPRECATED - use SECURITY)' },
        AUTHORIZATION: { icon: <IconLock />, color: 'green', description: 'Access control policies (DEPRECATED - use SECURITY)' },
        BUSINESS_RULE: { icon: <IconBusinessplan />, color: 'orange', description: 'Business logic rules (DEPRECATED - use VALIDATION)' }
    };

    // Handle policy creation/save
    const handleSavePolicy = async () => {
        try {
            const policyData = {
                ...policyMetadata,
                type: policyType,
                scope: policyScope,
                expression: implementation,
                rules,
                status: 'DRAFT',
                created_at: new Date().toISOString()
            };

            await createPolicy(policyData, {
                onSuccess: (result: any) => {
                    notifications.show({
                        title: 'Success',
                        message: `Policy "${policyMetadata.name}" created successfully`,
                        color: 'green'
                    });

                    if (onSave) {
                        onSave(result);
                    }

                    refetch();
                },
                showNotifications: false
            });
        } catch (err) {
            notifications.show({
                title: 'Error',
                message: `Failed to create policy: ${err instanceof Error ? err.message : 'Unknown error'}`,
                color: 'red'
            });
        }
    };

    // Handle policy testing
    const handleTestPolicy = async () => {
        if (!policyMetadata.name || !implementation) {
            notifications.show({
                title: 'Validation Error',
                message: 'Policy name and expression are required for testing',
                color: 'orange'
            });
            return;
        }

        try {
            let testPayload = {};
            try {
                testPayload = JSON.parse(testData);
            } catch (e) {
                notifications.show({
                    title: 'Invalid Test Data',
                    message: 'Test data must be valid JSON',
                    color: 'orange'
                });
                return;
            }

            const result = await testPolicy({
                variables: {
                    input: {
                        tenant: 'test',
                        kind: 'PolicyTest',
                        key: `test-${Date.now()}`,
                        draft: {
                            policy_name: policyMetadata.name,
                            expression: implementation,
                            test_data: testPayload,
                            type: policyType,
                            scope: policyScope
                        }
                    }
                }
            });

            setTestResult(result.data);

            notifications.show({
                title: 'Policy Test Complete',
                message: 'Policy test executed successfully',
                color: 'blue'
            });

        } catch (err) {
            notifications.show({
                title: 'Test Failed',
                message: `Policy test failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
                color: 'red'
            });
        }
    };

    const policyScopeConfig = {
        Global: { icon: <IconGlobe />, color: 'blue' },
        Tenant: { icon: <IconBuilding />, color: 'green' },
        Workflow: { icon: <IconGitBranch />, color: 'orange' },
        Field: { icon: <IconTextCaption />, color: 'purple' },
        Entity: { icon: <IconDatabase />, color: 'red' }
    };

    // Initialize Tabulator for Rules
    useEffect(() => {
        if (rulesTableRef.current && !rulesTabulator.current) {
            rulesTabulator.current = new TabulatorFull(rulesTableRef.current, {
                data: rules,
                layout: 'fitColumns',
                responsiveLayout: 'hide',
                movableRows: true,
                addRowPos: 'bottom',
                history: true,
                pagination: 'local',
                paginationSize: 10,
                columns: [
                    {
                        rowHandle: true,
                        formatter: "handle",
                        headerSort: false,
                        frozen: true,
                        width: 30,
                        minWidth: 30
                    },
                    {
                        title: 'Priority',
                        field: 'priority',
                        width: 80,
                        editor: mode === 'edit' ? 'number' : false,
                        formatter: (cell: any) => {
                            const value = cell.getValue();
                            const color = value <= 10 ? 'red' : value <= 50 ? 'orange' : 'green';
                            return `<span style="color: ${color}; font-weight: bold;">${value}</span>`;
                        }
                    },
                    {
                        title: 'Condition',
                        field: 'condition',
                        editor: mode === 'edit' ? 'textarea' : false,
                        formatter: (cell: any) => {
                            const value = cell.getValue();
                            return `<code style="font-size: 11px;">${value}</code>`;
                        }
                    },
                    {
                        title: 'Action',
                        field: 'action',
                        editor: mode === 'edit' ? 'input' : false,
                        formatter: (cell: any) => {
                            const value = cell.getValue();
                            const isAllow = value?.toLowerCase().includes('allow');
                            const icon = isAllow ? '✅' : '🚫';
                            return `${icon} ${value}`;
                        }
                    },
                    {
                        title: 'Description',
                        field: 'description',
                        editor: mode === 'edit' ? 'input' : false
                    },
                    {
                        title: 'Actions',
                        formatter: () => {
                            if (mode === 'view') return '';
                            return '<button class="edit-btn">✏️</button> <button class="delete-btn">🗑️</button>';
                        },
                        width: 100,
                        hozAlign: 'center',
                        cellClick: (e: MouseEvent, cell: any) => {
                            const target = e.target as HTMLElement;
                            if (target.classList.contains('edit-btn')) {
                                setSelectedRule(cell.getData() as PolicyRule);
                                setModalOpened(true);
                            } else if (target.classList.contains('delete-btn')) {
                                const newRules = rules.filter((_: any, idx) => idx !== cell.getRow().getPosition());
                                setRules(newRules);
                            }
                        }
                    }
                ]
            });
        }
    }, [rules, mode]);

    // Compliance Score Calculation
    const calculateComplianceScore = () => {
        const factors = [
            rules.length > 0 ? 25 : 0,
            implementation ? 25 : 0,
            data?.approved ? 25 : 0,
            data.tested ? 25 : 0
        ];
        return factors.reduce((a, b) => a + b, 0);
    };

    const complianceScore = calculateComplianceScore();

    return (
        <Container size="xl">
            <Paper shadow="sm" p="lg">
                <Group justify="space-between" mb="lg">
                    <div>
                        <Title order={2}>Policy Management</Title>
                        <Text c="dimmed">Configure and enforce business rules and compliance policies</Text>
                        {policies && Object.keys(policies).length > 0 && (
                            <Text size="sm" c="dimmed">{Object.keys(policies).length} existing policies loaded</Text>
                        )}
                    </div>
                    <Group>
                        <RingProgress
                            size={80}
                            thickness={8}
                            sections={[
                                {
                                    value: complianceScore, color:
                                        complianceScore >= 75 ? 'green' :
                                            complianceScore >= 50 ? 'orange' : 'red'
                                }
                            ]}
                            label={
                                <Text size="xs" ta="center">
                                    {complianceScore}%
                                    <br />
                                    <Text size="xs" color="dimmed">Compliance</Text>
                                </Text>
                            }
                        />
                        <Stack gap="xs">
                            <Badge
                                leftSection={policyTypeConfig[policyType as keyof typeof policyTypeConfig]?.icon}
                                color={policyTypeConfig[policyType as keyof typeof policyTypeConfig]?.color}
                                size="lg"
                            >
                                {policyType}
                            </Badge>
                            <Badge
                                leftSection={policyScopeConfig[policyScope as keyof typeof policyScopeConfig]?.icon}
                                color={policyScopeConfig[policyScope as keyof typeof policyScopeConfig]?.color}
                                variant="outline"
                            >
                                {policyScope} Scope
                            </Badge>
                        </Stack>
                        <Button
                            onClick={handleSavePolicy}
                            disabled={creating || !policyMetadata.name}
                            loading={creating}
                        >
                            Save Policy
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setTestModalOpen(true)}
                            disabled={!policyMetadata.name || !implementation}
                        >
                            Test Policy
                        </Button>
                    </Group>
                </Group>

                {/* Policy Metadata Form */}
                <Card withBorder mb="lg">
                    <Stack>
                        <Group grow>
                            <TextInput
                                label="Policy Name"
                                placeholder="Enter policy name"
                                value={policyMetadata.name}
                                onChange={(e: any) => setPolicyMetadata({ ...policyMetadata, name: e.target.value })}
                                required
                            />
                            <Select
                                label="Policy Type"
                                value={policyType}
                                onChange={(value: any) => setPolicyType(value || 'SECURITY')}
                                data={[
                                    // NEW types (Phase 1 migration complete)
                                    { value: 'APPROVAL', label: 'Approval - Approval routing & dual control (NEW)' },
                                    { value: 'SECURITY', label: 'Security - Access control & authorization (REPLACES Guard/Authorization)' },
                                    { value: 'VALIDATION', label: 'Validation - Data validation & business rules' },
                                    { value: 'COMPLIANCE', label: 'Compliance - Regulatory compliance' },
                                    { value: 'NOTIFICATION', label: 'Notification - Communication routing (NEW)' }
                                ]}
                            />
                        </Group>
                        <Group grow>
                            <Select
                                label="Scope"
                                value={policyScope}
                                onChange={(value: any) => setPolicyScope(value || 'GLOBAL')}
                                data={[
                                    // NEW scopes (Phase 1 migration complete)
                                    { value: 'WORKFLOW', label: 'Workflow - Entire workflow lifecycle' },
                                    { value: 'TASK', label: 'Task - Task-specific policies (NEW)' },
                                    { value: 'APPROVAL', label: 'Approval - Approval gates (NEW - CRITICAL for dual control)' },
                                    { value: 'GLOBAL', label: 'Global - Tenant-wide (all workflows)' }
                                ]}
                            />
                            <NumberInput
                                label="Priority"
                                value={policyMetadata.priority}
                                onChange={(value: any) => setPolicyMetadata({ ...policyMetadata, priority: value || 100 })}
                                min={1}
                                max={1000}
                            />
                        </Group>
                        <Group grow>
                            <Select
                                label="Enforcement Mode"
                                value={policyMetadata.enforcement_mode}
                                onChange={(value: any) => setPolicyMetadata({ ...policyMetadata, enforcement_mode: value || 'STRICT' })}
                                data={[
                                    { value: 'STRICT', label: 'Strict' },
                                    { value: 'WARN', label: 'Warn Only' },
                                    { value: 'DISABLED', label: 'Disabled' }
                                ]}
                            />
                            <TextInput
                                label="Description"
                                placeholder="Policy description"
                                value={policyMetadata.description}
                                onChange={(e: any) => setPolicyMetadata({ ...policyMetadata, description: e.target.value })}
                            />
                        </Group>
                    </Stack>
                </Card>

                <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? '')}>
                    <Tabs.List>
                        <Tabs.Tab value="rules" leftSection={<IconShield size={16} />}>
                            Rules ({rules.length})
                        </Tabs.Tab>
                        <Tabs.Tab value="implementation" leftSection={<IconBusinessplan size={16} />}>
                            Implementation
                        </Tabs.Tab>
                        <Tabs.Tab value="testing" leftSection={<IconFileCheck size={16} />}>
                            Testing
                        </Tabs.Tab>
                        <Tabs.Tab value="compliance" leftSection={<IconScale size={16} />}>
                            Compliance
                        </Tabs.Tab>
                        <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>
                            History
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="rules">
                        <Stack mt="md">
                            <Group justify="space-between">
                                <Group>
                                    <Select
                                        label="Policy Type"
                                        value={policyType}
                                        onChange={(v) => setPolicyType(v ?? '')}
                                        data={Object.keys(policyTypeConfig)}
                                        style={{ width: 200 }}
                                    />
                                    <Select
                                        label="Scope"
                                        value={policyScope}
                                        onChange={(v) => setPolicyScope(v ?? '')}
                                        data={Object.keys(policyScopeConfig)}
                                        style={{ width: 150 }}
                                    />
                                </Group>
                                <Button
                                    leftSection={<IconPlus size={16} />}
                                    onClick={() => {
                                        setSelectedRule(null);
                                        setModalOpened(true);
                                    }}
                                >
                                    Add Rule
                                </Button>
                            </Group>

                            <Alert icon={<IconInfoCircle size={16} />} color="blue">
                                Rules are evaluated in priority order. Lower numbers have higher priority.
                            </Alert>

                            <div ref={rulesTableRef} />
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="implementation">
                        <Stack mt="md">
                            <Alert icon={<IconBusinessplan size={16} />} color="orange">
                                Implementation defines the policy logic using CEL expressions or custom code
                            </Alert>

                            <SegmentedControl
                                data={[
                                    { label: 'CEL Expression', value: 'cel' },
                                    { label: 'JavaScript', value: 'js' },
                                    { label: 'Cedar Policy', value: 'cedar' },
                                    { label: 'JSON Logic', value: 'json' }
                                ]}
                            />

                            <Textarea
                                label="Policy Implementation"
                                description="Define your policy logic here"
                                placeholder="// Example CEL expression
user.role == 'admin' && resource.sensitive == false"
                                minRows={15}
                                value={implementation}
                                onChange={(e: any) => setImplementation(e.currentTarget.value)}
                                styles={{ input: { fontFamily: 'monospace' } }}
                            />

                            <Grid>
                                <Grid.Col span={6}>
                                    <Card withBorder>
                                        <Group justify="space-between" mb="xs">
                                            <Text fw={500}>Available Variables</Text>
                                            <IconInfoCircle size={16} />
                                        </Group>
                                        <List size="sm">
                                            <List.Item>user - Current user context</List.Item>
                                            <List.Item>resource - Resource being accessed</List.Item>
                                            <List.Item>action - Action being performed</List.Item>
                                            <List.Item>context - Execution context</List.Item>
                                            <List.Item>request - Request parameters</List.Item>
                                        </List>
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <Card withBorder>
                                        <Group justify="space-between" mb="xs">
                                            <Text fw={500}>Available Functions</Text>
                                            <IconInfoCircle size={16} />
                                        </Group>
                                        <List size="sm">
                                            <List.Item>hasRole(role) - Check user role</List.Item>
                                            <List.Item>hasPermission(perm) - Check permission</List.Item>
                                            <List.Item>inTimeRange(start, end) - Time check</List.Item>
                                            <List.Item>matchesPattern(pattern) - Regex match</List.Item>
                                            <List.Item>evaluate(expr) - Evaluate expression</List.Item>
                                        </List>
                                    </Card>
                                </Grid.Col>
                            </Grid>
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="testing">
                        <Stack mt="md">
                            <Alert icon={<IconFileCheck size={16} />} color="green">
                                Test your policy against different scenarios to ensure correct behavior
                            </Alert>

                            <Grid>
                                <Grid.Col span={6}>
                                    <Card withBorder>
                                        <Title order={5} mb="md">Test Input</Title>
                                        <JsonInput
                                            label="Context"
                                            placeholder="Enter test context"
                                            minRows={10}
                                            defaultValue={JSON.stringify({
                                                user: { id: '123', role: 'admin' },
                                                resource: { type: 'document', sensitive: false },
                                                action: 'read'
                                            }, null, 2)}
                                        />
                                        <Button fullWidth mt="md" leftSection={<IconPlayerPlay size={16} />}>
                                            Run Test
                                        </Button>
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <Card withBorder>
                                        <Title order={5} mb="md">Test Results</Title>
                                        <Stack>
                                            <Group justify="space-between">
                                                <Text>Decision:</Text>
                                                <Badge color="green" size="lg">ALLOW</Badge>
                                            </Group>
                                            <Divider />
                                            <Text size="sm" fw={500}>Matched Rules:</Text>
                                            <List size="sm">
                                                <List.Item icon={<IconCheck size={16} color="green" />}>
                                                    Rule 1: Admin access granted
                                                </List.Item>
                                                <List.Item icon={<IconCheck size={16} color="green" />}>
                                                    Rule 2: Non-sensitive resource
                                                </List.Item>
                                            </List>
                                            <Divider />
                                            <Text size="sm" fw={500}>Execution Time:</Text>
                                            <Text size="sm" color="dimmed">2.3ms</Text>
                                        </Stack>
                                    </Card>
                                </Grid.Col>
                            </Grid>

                            <Card withBorder>
                                <Title order={5} mb="md">Test Scenarios</Title>
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Scenario</th>
                                            <th>Expected</th>
                                            <th>Actual</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Admin access to public resource</td>
                                            <td><Badge color="green">Allow</Badge></td>
                                            <td><Badge color="green">Allow</Badge></td>
                                            <td><IconCheck size={16} color="green" /></td>
                                            <td>
                                                <ActionIcon size="sm"><IconEye size={14} /></ActionIcon>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>User access to sensitive resource</td>
                                            <td><Badge color="red">Deny</Badge></td>
                                            <td><Badge color="red">Deny</Badge></td>
                                            <td><IconCheck size={16} color="green" /></td>
                                            <td>
                                                <ActionIcon size="sm"><IconEye size={14} /></ActionIcon>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Guest access to API</td>
                                            <td><Badge color="red">Deny</Badge></td>
                                            <td><Badge color="green">Allow</Badge></td>
                                            <td><IconAlertTriangle size={16} color="orange" /></td>
                                            <td>
                                                <ActionIcon size="sm"><IconEye size={14} /></ActionIcon>
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Card>
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="compliance">
                        <Stack mt="md">
                            <Grid>
                                <Grid.Col span={4}>
                                    <Card withBorder>
                                        <Group justify="space-between" mb="md">
                                            <Text fw={500}>Compliance Status</Text>
                                            <ThemeIcon
                                                color={complianceScore >= 75 ? 'green' : 'orange'}
                                                size="lg"
                                                variant="light"
                                            >
                                                <IconShieldCheck size={20} />
                                            </ThemeIcon>
                                        </Group>
                                        <Progress value={complianceScore} color={complianceScore >= 75 ? 'green' : 'orange'} mb="xs" />
                                        <Text size="sm" color="dimmed">{complianceScore}% compliant</Text>

                                        <Stack mt="md" gap="xs">
                                            <Group justify="space-between">
                                                <Text size="sm">Rules Defined</Text>
                                                {rules.length > 0 ?
                                                    <IconCheck size={16} color="green" /> :
                                                    <IconAlertTriangle size={16} color="orange" />
                                                }
                                            </Group>
                                            <Group justify="space-between">
                                                <Text size="sm">Implementation</Text>
                                                {implementation ?
                                                    <IconCheck size={16} color="green" /> :
                                                    <IconAlertTriangle size={16} color="orange" />
                                                }
                                            </Group>
                                            <Group justify="space-between">
                                                <Text size="sm">Approved</Text>
                                                {data?.approved ?
                                                    <IconCheck size={16} color="green" /> :
                                                    <IconAlertTriangle size={16} color="orange" />
                                                }
                                            </Group>
                                            <Group justify="space-between">
                                                <Text size="sm">Tested</Text>
                                                {data.tested ?
                                                    <IconCheck size={16} color="green" /> :
                                                    <IconAlertTriangle size={16} color="orange" />
                                                }
                                            </Group>
                                        </Stack>
                                    </Card>
                                </Grid.Col>

                                <Grid.Col span={8}>
                                    <Card withBorder>
                                        <Title order={5} mb="md">Compliance Requirements</Title>
                                        <Accordion>
                                            <Accordion.Item value="gdpr">
                                                <Accordion.Control icon={<IconScale size={16} />}>
                                                    GDPR Compliance
                                                </Accordion.Control>
                                                <Accordion.Panel>
                                                    <List size="sm">
                                                        <List.Item icon={<IconCheck size={16} color="green" />}>
                                                            Data minimization principle enforced
                                                        </List.Item>
                                                        <List.Item icon={<IconCheck size={16} color="green" />}>
                                                            Consent verification implemented
                                                        </List.Item>
                                                        <List.Item icon={<IconAlertTriangle size={16} color="orange" />}>
                                                            Right to erasure needs review
                                                        </List.Item>
                                                    </List>
                                                </Accordion.Panel>
                                            </Accordion.Item>

                                            <Accordion.Item value="hipaa">
                                                <Accordion.Control icon={<IconScale size={16} />}>
                                                    HIPAA Compliance
                                                </Accordion.Control>
                                                <Accordion.Panel>
                                                    <List size="sm">
                                                        <List.Item icon={<IconCheck size={16} color="green" />}>
                                                            PHI access controls in place
                                                        </List.Item>
                                                        <List.Item icon={<IconCheck size={16} color="green" />}>
                                                            Audit logging enabled
                                                        </List.Item>
                                                        <List.Item icon={<IconCheck size={16} color="green" />}>
                                                            Encryption at rest configured
                                                        </List.Item>
                                                    </List>
                                                </Accordion.Panel>
                                            </Accordion.Item>

                                            <Accordion.Item value="sox">
                                                <Accordion.Control icon={<IconScale size={16} />}>
                                                    SOX Compliance
                                                </Accordion.Control>
                                                <Accordion.Panel>
                                                    <List size="sm">
                                                        <List.Item icon={<IconCheck size={16} color="green" />}>
                                                            Financial data access restricted
                                                        </List.Item>
                                                        <List.Item icon={<IconAlertTriangle size={16} color="orange" />}>
                                                            Segregation of duties pending
                                                        </List.Item>
                                                    </List>
                                                </Accordion.Panel>
                                            </Accordion.Item>
                                        </Accordion>
                                    </Card>
                                </Grid.Col>
                            </Grid>
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="history">
                        <Stack mt="md">
                            <Timeline active={2} lineWidth={2}>
                                <Timeline.Item bullet={<IconUserCheck size={12} />} title="Policy Created">
                                    <Text color="dimmed" size="sm">Created by John Doe</Text>
                                    <Text size="xs" mt={4}>2024-01-10 10:30 AM</Text>
                                </Timeline.Item>

                                <Timeline.Item bullet={<IconEdit size={12} />} title="Rules Updated">
                                    <Text color="dimmed" size="sm">Added 3 new validation rules</Text>
                                    <Text size="xs" mt={4}>2024-01-11 2:15 PM</Text>
                                </Timeline.Item>

                                <Timeline.Item bullet={<IconFileCheck size={12} />} title="Testing Completed">
                                    <Text color="dimmed" size="sm">All test scenarios passed</Text>
                                    <Text size="xs" mt={4}>2024-01-12 11:00 AM</Text>
                                </Timeline.Item>

                                <Timeline.Item bullet={<IconShieldCheck size={12} />} title="Approved for Production" lineVariant="dashed">
                                    <Text color="dimmed" size="sm">Approved by Security Team</Text>
                                    <Text size="xs" mt={4}>2024-01-13 9:30 AM</Text>
                                </Timeline.Item>
                            </Timeline>
                        </Stack>
                    </Tabs.Panel>
                </Tabs>
            </Paper>

            {/* Modal for editing rules */}
            <Modal
                opened={modalOpened}
                onClose={() => setModalOpened(false)}
                title={selectedRule ? 'Edit Rule' : 'Add New Rule'}
                size="lg"
            >
                <Stack>
                    <NumberInput
                        label="Priority"
                        description="Lower numbers have higher priority"
                        placeholder="Enter priority (1-100)"
                        min={1}
                        max={100}
                        defaultValue={selectedRule?.priority || 50}
                    />

                    <Textarea
                        label="Condition"
                        description="CEL expression that evaluates to true/false"
                        placeholder="user.role == 'admin' && resource.type == 'document'"
                        minRows={3}
                        defaultValue={selectedRule?.condition || ''}
                    />

                    <Select
                        label="Action"
                        description="Action to take when condition is met"
                        data={[
                            { value: 'allow', label: '✅ Allow' },
                            { value: 'deny', label: '🚫 Deny' },
                            { value: 'require_mfa', label: '🔐 Require MFA' },
                            { value: 'log', label: '📝 Log Only' },
                            { value: 'redirect', label: '↪️ Redirect' }
                        ]}
                        defaultValue={selectedRule?.action || 'allow'}
                    />

                    <TextInput
                        label="Description"
                        description="Brief description of this rule"
                        placeholder="Allow admins to access all documents"
                        defaultValue={selectedRule?.description || ''}
                    />

                    <Group justify="flex-end">
                        <Button variant="subtle" onClick={() => setModalOpened(false)}>
                            Cancel
                        </Button>
                        <Button onClick={() => {
                            setModalOpened(false);
                            // Save logic here
                        }}>
                            Save Rule
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            {/* Policy Test Modal */}
            <Modal
                opened={testModalOpen}
                onClose={() => setTestModalOpen(false)}
                title="Test Policy"
                size="lg"
            >
                <Stack>
                    <Alert color="blue">
                        Test your policy expression against sample data to verify it works as expected.
                    </Alert>

                    <JsonInput
                        label="Test Data"
                        description="Provide JSON data to test the policy against"
                        placeholder='{"user": {"role": "admin"}, "document": {"type": "sensitive"}}'
                        value={testData}
                        onChange={setTestData}
                        minRows={4}
                        autosize
                        formatOnBlur
                    />

                    <Textarea
                        label="Policy Expression"
                        value={implementation}
                        onChange={(e: any) => setImplementation(e.target.value)}
                        minRows={3}
                        placeholder="user.role == 'admin' || document.type != 'sensitive'"
                    />

                    {testResult && (
                        <Card withBorder>
                            <Text fw={500} mb="xs">Test Result</Text>
                            <Code block>{JSON.stringify(testResult, null, 2)}</Code>
                        </Card>
                    )}

                    <Group justify="flex-end">
                        <Button
                            variant="outline"
                            onClick={() => setTestModalOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleTestPolicy}
                            loading={testing}
                            disabled={!implementation}
                        >
                            Run Test
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            {/* Integrated Search Menu */}
            <WorkflowSearchMenu
                workflowType="Policy"
                workflowIcon="🛡️"
                workflowColor="purple"
                position="bottom"
                height="350px"
                onItemSelect={(item) => {
                    console.log('Selected policy:', item);
                    // Handle item selection
                }}
                onSearch={(query, filters) => {
                    console.log('Search query:', query, 'Filters:', filters);
                    // Handle search
                }}
            />
        </Container>
    );
};

export default PolicyEnhanced;
